import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Button,
  useDisclosure,
  Stack,
  Image,
  Link,
  Container,
  useBreakpointValue,
  Collapse,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon } from '@chakra-ui/icons';

const Links = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' },
  { name: 'Programs', path: '/programs' },
  { name: 'Target Groups', path: '/target-groups' },
  { name: 'Get Involved', path: '/get-involved' },
  { name: 'Impact Stories', path: '/impact-stories' },
  { name: 'Contact Us', path: '/contact' },
];

const Header: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  
  // Check if we're on the homepage
  const isHomePage = location.pathname === '/';
  
  // Determine if header should be transparent based on page and scroll position
  const isTransparent = isHomePage && !scrolled;
  
  // Handle scroll event to change header appearance
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 60) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Responsive logo size
  const logoSize = useBreakpointValue({ base: '120px', md: '150px' });

  return (
    <Box
      as="header"
      position="fixed"
      top="0"
      left="0"
      right="0"
      zIndex="1000"
      bg={scrolled ? 'white' : 'rgba(255, 255, 255, 0.8)'}
      boxShadow={scrolled ? 'md' : 'none'}
      transition="all 0.3s ease"
    >
      <Container maxW="container.xl">
        <Flex
          h={{ base: '70px', md: '80px' }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }} align="center">
            <Link
              as={RouterLink}
              to="/"
              textAlign="center"
              _hover={{
                textDecoration: 'none',
              }}
            >
              <Flex align="center">
                <Image 
                  src="/assets/logo.png" 
                  alt="ACTION FOR LIFE PEACE AND HOPE ADVANCEMENT (ALPHA) Logo" 
                  boxSize={{ base: "40px", md: "50px" }} 
                  mr={2} 
                  borderRadius="full"
                />
                <Box 
                  fontFamily="heading"
                  fontWeight="bold"
                  color="brand.500"
                  fontSize={{ base: "lg", md: "xl" }}
                >
                  ALPHA
                </Box>
              </Flex>
            </Link>
          </Flex>

          <Flex display={{ base: 'none', md: 'flex' }} alignItems="center">
            <HStack spacing={8} alignItems="center">
              <HStack as="nav" spacing={6} display={{ base: 'none', md: 'flex' }}>
                {Links.map((link) => (
                  <Link
                    key={link.name}
                    as={RouterLink}
                    to={link.path}
                    px={2}
                    py={1}
                    rounded="md"
                    fontWeight="500"
                    position="relative"
                    _hover={{
                      textDecoration: 'none',
                      color: 'brand.500',
                    }}
                    sx={{
                      position: 'relative',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        width: location.pathname === link.path ? '100%' : '0%',
                        height: '2px',
                        bottom: '-4px',
                        left: '0',
                        bg: 'brand.500',
                        transition: 'width 0.3s ease',
                      },
                      '&:hover::after': {
                        width: '100%',
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </HStack>
            </HStack>
            <Button
              as={RouterLink}
              to="/get-involved"
              variant="primary"
              size="md"
              ml={8}
            >
              Donate
            </Button>
          </Flex>

          <IconButton
            display={{ base: 'flex', md: 'none' }}
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant="ghost"
            aria-label="Toggle Navigation"
            color={isTransparent ? 'white' : 'gray.800'}
            _hover={{
              bg: isTransparent ? 'whiteAlpha.200' : 'gray.100',
            }}
          />
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <Box
            pb={4}
            display={{ md: 'none' }}
            bg={isTransparent ? 'blackAlpha.700' : 'white'}
            borderRadius="md"
          >
            <Stack as="nav" spacing={4} p={4}>
              {Links.map((link) => (
                <Link
                  key={link.name}
                  as={RouterLink}
                  to={link.path}
                  px={2}
                  py={2}
                  rounded="md"
                  fontWeight="500"
                  _hover={{
                    textDecoration: 'none',
                    bg: isTransparent ? 'whiteAlpha.200' : 'gray.100',
                  }}
                  onClick={onToggle}
                >
                  {link.name}
                </Link>
              ))}
              <Button variant="primary" size="sm" as={RouterLink} to="/get-involved">
                Donate
              </Button>
            </Stack>
          </Box>
        </Collapse>
      </Container>
    </Box>
  );
};

export default Header;
